import PageLayout from "components/layout/PageLayout"
import { useTranslation } from "react-i18next"
import { useLocation, useNavigate } from "react-router-dom"
import { useEffect, useState } from "react"
import { Dialog } from "primereact/dialog"
import {
    useLazyGetDeviceLogsQuery,
    useClearDeviceLogsMutation
} from "./api/deviceDiagnosticsApiSlice"
import DeviceCard from "../dashboard/view/DeviceCard"

const SEVERITY_CONFIG = {
    DEBUG: { bgClass: "bg-primary",   textClass: "" },
    INFO:  { bgClass: "bg-light",     textClass: "text-dark" },
    WARN:  { bgClass: "bg-warning",   textClass: "text-dark" },
    ERROR: { bgClass: "bg-danger",    textClass: "" }
}

const PAGE_SIZE = 20

export default function DeviceLogDetail() {
    const [t] = useTranslation(["global", "device"])
    const location = useLocation()
    const navigate = useNavigate()

    // deviceGuid: from DiagnosticCard navigation; guid: from NotificationCard navigation
    const deviceGuid = location.state?.deviceGuid ?? location.state?.guid
    const [page, setPage] = useState(0)
    const [showClearDialog, setShowClearDialog] = useState(false)

    const [getLogs, {
        data,
        isLoading,
        isFetching,
        isSuccess,
        isError,
        error
    }] = useLazyGetDeviceLogsQuery()

    const [clearLogs, { isLoading: isClearing }] = useClearDeviceLogsMutation()

    useEffect(() => {
        if (deviceGuid) {
            getLogs({ deviceGuid, page, pageSize: PAGE_SIZE })
        }
    }, [getLogs, deviceGuid, page])

    const handleClearConfirm = async () => {
        await clearLogs(deviceGuid)
        setShowClearDialog(false)
        setPage(0)
    }

    const device = data?.device
    const logs = data?.logs

    const formatTimestamp = (ts) => new Date(ts).toLocaleString()

    const pageTitle = device
        ? `${t("device:diagnostics.device_logs_title")} — ${device.model}`
        : t("device:diagnostics.device_logs_title")

    const content = (
        <>
            {/* Toolbar */}
            <div className="d-flex align-items-center gap-2 mb-3 flex-wrap">
                <button className="btn btn-secondary btn-sm" onClick={() => navigate(-1)}>
                    <i className="fa-solid fa-arrow-left me-1" />
                    {t("device:diagnostics.back_to_diagnostics")}
                </button>
                <button
                    className="btn btn-outline-danger btn-sm ms-auto"
                    onClick={() => setShowClearDialog(true)}
                    disabled={!logs?.totalElements}
                >
                    <i className="fa-solid fa-trash me-1" />
                    {t("device:diagnostics.clear_logs")}
                </button>
            </div>

            {/* Device summary row */}
            {device && (
                <div className=" mb-4">
                    <table className="table table-dark align-middle mb-0">
                        <thead>
                            <tr>
                                <th>ID</th>
                                <th>{t("device:entity.device.props.enabled")}</th>
                                <th>IP</th>
                                <th>{t("device:entity.device.props.model")}</th>
                                <th>{t("device:entity.device.props.battery")}</th>
                                <th>{t("device:entity.device.props.state")}</th>
                                <th>{t("device:entity.device.props.info")}</th>
                                <th>{t("device:entity.device.props.last_user")}</th>
                                <th>{t("property.actions")}</th>
                            </tr>
                        </thead>
                        <tbody>
                            <DeviceCard device={device} />
                        </tbody>
                    </table>
                </div>
            )}

            {isSuccess && logs && (
                <>
                    <div className="table-responsive">
                        <table className="table table-dark table-hover align-middle">
                            <thead>
                                <tr>
                                    <th style={{ width: "8rem" }}>{t("device:diagnostics.log.severity")}</th>
                                    <th>{t("device:diagnostics.log.message")}</th>
                                    <th style={{ width: "12rem" }}>{t("device:diagnostics.log.timestamp")}</th>
                                </tr>
                            </thead>
                            <tbody>
                                {logs.content?.length > 0
                                    ? logs.content.map((log, idx) => {
                                        const cfg = SEVERITY_CONFIG[log.severity] ?? SEVERITY_CONFIG.INFO
                                        return (
                                            <tr key={idx}>
                                                <td>
                                                    <span className={`badge ${cfg.bgClass} ${cfg.textClass}`}>
                                                        {log.severity}
                                                    </span>
                                                </td>
                                                <td>
                                                    <code className="text-light">{log.message}</code>
                                                </td>
                                                <td>
                                                    <small className="text-muted">
                                                        {formatTimestamp(log.timestamp*1000)}
                                                    </small>
                                                </td>
                                            </tr>
                                        )
                                    })
                                    : (
                                        <tr>
                                            <td colSpan={3} className="text-center text-muted py-4">
                                                {t("device:diagnostics.no_logs")}
                                            </td>
                                        </tr>
                                    )
                                }
                            </tbody>
                        </table>
                    </div>

                    {/* Pagination */}
                    {logs.totalPages > 1 && (
                        <nav>
                            <ul className="pagination justify-content-center">
                                <li className={`page-item ${page === 0 ? "disabled" : ""}`}>
                                    <button className="page-link" onClick={() => setPage(p => p - 1)}>
                                        <i className="fa-solid fa-chevron-left" />
                                    </button>
                                </li>
                                <li className="page-item disabled">
                                    <span className="page-link">
                                        {page + 1} / {logs.totalPages}
                                    </span>
                                </li>
                                <li className={`page-item ${page >= logs.totalPages - 1 ? "disabled" : ""}`}>
                                    <button className="page-link" onClick={() => setPage(p => p + 1)}>
                                        <i className="fa-solid fa-chevron-right" />
                                    </button>
                                </li>
                            </ul>
                        </nav>
                    )}
                </>
            )}

            <Dialog
                header={t("device:diagnostics.clear_logs_confirm_title")}
                visible={showClearDialog}
                onHide={() => setShowClearDialog(false)}
            >
                <p>{t("device:diagnostics.clear_logs_confirm_msg", { device: device?.model })}</p>
                <div className="text-end mt-3">
                    <button
                        className="btn btn-danger me-2"
                        onClick={handleClearConfirm}
                        disabled={isClearing}
                    >
                        {t("action.confirm")}
                    </button>
                    <button className="btn btn-secondary" onClick={() => setShowClearDialog(false)}>
                        {t("action.cancel")}
                    </button>
                </div>
            </Dialog>
        </>
    )

    return (
        <PageLayout
            pageTitle={pageTitle}
            bodyContent={content}
            isLoadingPage={isLoading}
            isLoadingContent={isFetching || isClearing}
            isError={isError}
            errorMsg={error}
            onPageRefresh={() => getLogs({ deviceGuid, page, pageSize: PAGE_SIZE })}
        />
    )
}
